import React, { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';
import { CheckCircle2, ExternalLink, RefreshCw, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const planLabel = (plan) => ({
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
}[plan] || plan);

const statusClass = (status) => ({
  pending: 'badge-pending',
  approved: 'badge-approved',
  rejected: 'badge-rejected',
}[status] || 'badge-trial');

const AccessRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/super-admin/access-requests');
      setRequests(res.data.requests);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const approveRequest = async (id) => {
    if (!window.confirm('Approve this request after manually verifying payment?')) return;
    setActionId(id);
    try {
      await api.post(`/super-admin/access-requests/${id}/approve`);
      toast.success('Request approved and tenant created');
      fetchRequests();
    } finally {
      setActionId(null);
    }
  };

  const rejectRequest = async (id) => {
    const reason = window.prompt('Enter rejection reason');
    if (!reason || !reason.trim()) return;
    setActionId(id);
    try {
      await api.post(`/super-admin/access-requests/${id}/reject`, { reason });
      toast.success('Request rejected');
      fetchRequests();
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Access Requests</h1>
          <p className="text-gray-500 text-sm mt-1">Review self-registration requests and manual payments</p>
        </div>
        <button onClick={fetchRequests} className="btn-secondary">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="card !p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Organization Name', 'Contact Person', 'Organization Email', 'Selected Plan', 'Payment Screenshot', 'Transaction ID', 'Request Date', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="text-center py-12 text-gray-400">Loading...</td></tr>
              ) : requests.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-12 text-gray-400">No registration requests found.</td></tr>
              ) : requests.map(req => (
                <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900">{req.organization_name}</td>
                  <td className="px-4 py-3 text-gray-600">
                    <div>{req.admin_full_name}</div>
                    <div className="text-xs text-gray-400">{req.work_email}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{req.organization_email}</td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-900">{planLabel(req.plan)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <a
                      href={`${API_ORIGIN}${req.payment_screenshot_path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary-600 hover:underline inline-flex items-center gap-1"
                    >
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{req.transaction_id || '-'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{format(new Date(req.created_at), 'MMM d, yyyy')}</td>
                  <td className="px-4 py-3"><span className={statusClass(req.status)}>{req.status}</span></td>
                  <td className="px-4 py-3">
                    {req.status === 'pending' ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => approveRequest(req.id)}
                          disabled={actionId === req.id}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                          title="Approve"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => rejectRequest(req.id)}
                          disabled={actionId === req.id}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded disabled:opacity-50"
                          title="Reject"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">{req.rejection_reason || '-'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccessRequests;
